const {
	AJAX_TIMEOUT,
	uniqBy,
	$each,
	isShallowEqual,
	runAjax,
	logMarketingEvent,
	onClickOrEnter,
	compileTemplate,
	sendDataLayerEvent,
} = require("./ElevHelpers");
const {openModal, closeCurrentModal} = require("./ModalUtil");
const ElevPrices = require("./ElevPrices");
const {track} = require("../../../core/util/Mixpanel");
const ShopData = require("../../../../shared/data/dbShop.json");
const ShopBundleData = require("../../../../shared/data/dbShopBundle.json");
const {MAX_SUBS} = require("../../../../shared/Constants");
const {
	getItemCountWithBundlesAndSets,
	getSubscriptionPlaceholderName,
} = require("../../../../shared/StoreUtil");
const {validateItems} = require("../../../../shared/StoreBundleUtil");
const ErrorUtil = require("../../../../shared/ErrorUtil");
const {getFeatureFlag} = require("../../../core/util/FeatureFlags");

//
// Cart item utilities
//

function getGradeSetItems(grade, quantity, options) {
	options = options || {};

	const practiceItems =
		Number(grade) !== 1
			? [
					{sku: "beast-" + grade + "a-practice", quantity, options},
					{sku: "beast-" + grade + "b-practice", quantity, options},
					{sku: "beast-" + grade + "c-practice", quantity, options},
					{sku: "beast-" + grade + "d-practice", quantity, options},
			  ]
			: [];

	return [
		{sku: "beast-" + grade + "a", quantity, options},
		{sku: "beast-" + grade + "b", quantity, options},
		{sku: "beast-" + grade + "c", quantity, options},
		{sku: "beast-" + grade + "d", quantity, options},
		...practiceItems,
	];
}

function getScienceGradeSetItems(grade, quantity, options) {
	options = options || {};

	return ["a", "b"].map(bookLetter => ({
		sku: `beast-${grade}${bookLetter}-science`,
		quantity,
		options,
	}));
}

function getBundleItems(subName, grades) {
	let items = [];
	grades.forEach(grade => {
		const bundleID = "bundle-grade-" + grade;
		items = items.concat(
			getGradeSetItems(grade, 1, {bundleID}).concat([
				{
					sku: "beast-yearly",
					quantity: 1,
					options: {displayName: subName, bundleID},
				},
			])
		);
	});
	return items;
}

// Marks a freshly-built bundle as having a deferred book level (placement-test
// flow). The cart still holds the full provisional bundle; only the yearly
// subscription line is flagged. bookLevelDeferred gates the AoPS transfer and
// provisionalGrade records the level chosen as a starting point. See
// shared/StoreUtil.js isDeferredBundleSubscription.
function withDeferredBookLevel(items, grade) {
	return items.map(item =>
		item.sku === "beast-yearly"
			? {
					...item,
					options: {
						...item.options,
						bookLevelDeferred: true,
						provisionalGrade: Number(grade),
					},
			  }
			: item
	);
}

// Only the first argument is required, and grades only matters for bundles. A
// placeholder name and a default grade will be used if they are omitted.
function getItemsForSubType(subType, subName, grades) {
	subName = subName || "name";
	grades = grades || [3];
	if (subType === "monthly") {
		return [
			{
				sku: "beast-monthly",
				quantity: 1,
				options: {displayName: subName},
			},
		];
	} else if (subType === "yearly") {
		return [
			{
				sku: "beast-yearly",
				quantity: 1,
				options: {displayName: subName},
			},
		];
	} else if (subType === "bundle") {
		return getBundleItems(subName, grades);
	}
}

//
// JS for cart
//

// The cart is loaded fresh from storage basically every time it is used, but
// here are some extra flags specific to the page load.
const cartState = {
	doingFirstSave: false,
	needsSave: false,
};

function getDefaultCart() {
	return {
		cartID: null,
		items: [],
		lastItemsUpdate: 0,
	};
}

function getTotalQuantity(items) {
	if (!Array.isArray(items)) return 0;
	return items.reduce((acc, item) => acc + item.quantity, 0);
}

function getItemCount(cart) {
	const nonBundleItems = cart && cart.items.filter(i => !i.options.bundleID);

	let bundleItems =
		cart &&
		cart.items.filter(i => i.options.bundleID && i.sku === "beast-yearly");
	bundleItems = uniqBy(bundleItems, i => i.options.displayName);

	return getTotalQuantity(nonBundleItems.concat(bundleItems));
}

/*
   Returns the number of yearly/monthly subs in the cart.
   Note that a bundle counts as 1 subscription.
*/

function countSubscriptionsInCart(cart) {
	if (!cart || !cart.items) return 0;
	return cart.items.reduce((sum, i) => {
		if (i.sku === "beast-yearly" || i.sku === "beast-monthly") {
			return sum + (i.quantity || 1);
		}
		return sum;
	}, 0);
}

/* Equivalent for canAddSubRow function in EnrollUtil.js,
   but here we load cart from storage */
function canAddSubToCart() {
	const cart = loadCartFromStorage();
	const subCount = countSubscriptionsInCart(cart);
	return subCount < MAX_SUBS;
}

// Returns true for two item objects that should be combined in the cart.
function isSameItemType(a, b) {
	if (!a || !b) return a === b;
	if (a.sku !== b.sku) return false;
	if (!isShallowEqual(a.options, b.options)) return false;
	return true;
}

// Returns the index of an item in the cart that matches the passed item
// under isSameItemType, or -1 if none was found.
function findSameItemIndex(item, cart) {
	for (let i = 0; i < cart.items.length; i++) {
		if (isSameItemType(item, cart.items[i])) {
			return i;
		}
	}
	return -1;
}

// Helper for addItemsToCart. Don't use by itself because analytics
// are in addItemsToCart to avoid multiple calls when several items
// added at once.
function addItemToCart(item, cart) {
	const index = findSameItemIndex(item, cart);
	if (index >= 0) {
		cart.items[index].quantity += item.quantity;
	} else {
		cart.items.push(item);
	}
}

function addItemsToCart(items, cart) {
	items.forEach(item => addItemToCart(item, cart));
	sendDataLayerEvent("add_to_cart", {
		// this one follows GA4 structure
		items: items.map(cartItem => {
			return {
				item_id: cartItem.sku,
				item_category: getItemTypeFromSku(cartItem.sku),
				quantity: cartItem.quantity,
			};
		}),
		// this one follows FB structure
		contents: items.map(cartItem => {
			return {
				id: cartItem.sku,
				quantity: cartItem.quantity,
			};
		}),
		content_type: "product",
		content_ids: items.map(item => item.sku),
	});
}

function removeItemFromCart(item, qtyToRemove, cart) {
	const index = findSameItemIndex(item, cart);
	if (index >= 0) {
		if (cart.items[index].quantity > qtyToRemove) {
			cart.items[index].quantity -= qtyToRemove;
		} else {
			cart.items.splice(index, 1);
		}
	}
}

function loadCartFromStorage() {
	try {
		const cartDataRaw = window.localStorage.getItem("cartData");
		if (!cartDataRaw) return getDefaultCart();
		const cartData = JSON.parse(cartDataRaw);
		const storedFinishedID = parseInt(
			window.localStorage.getItem("finishedCartID"),
			10
		);
		if (cartData && cartData.cartID === storedFinishedID) {
			return getDefaultCart();
		}
		if (!validateItems(cartData.items)) {
			return getDefaultCart();
		}
		return cartData;
	} catch (ex) {
		ErrorUtil.log(
			"E_COULDNT_LOAD_CART_FROM_STORAGE",
			"Couldn't load cart from storage.",
			{ex}
		);
		// If this turns out to be the issue, we may need to figure out how to get the cart from the server
		return getDefaultCart();
	}
}

function clearLocalStorageTempData() {
	const storage = window.localStorage;
	storage.removeItem("demoData");
	storage.removeItem("tempUserData");
	storage.removeItem("tempSharedProblems");
}

function checkLocalStorageIsFull() {
	const key = "localStorageTestKey";
	const storage = window.localStorage;

	// testing if LS is at capacity like in Storage.js
	try {
		storage.setItem(key, key);
		storage.removeItem(key);
	} catch (ex) {
		// LS could not set the item, likely to be full
		return true;
	}
	return false;
}

function saveCartToStorage(cart) {
	try {
		const storage = window.localStorage;
		const storedFinishedID = parseInt(
			window.localStorage.getItem("finishedCartID"),
			10
		);
		if (cart.cartID === storedFinishedID) {
			return;
		}
		storage.setItem("cartData", JSON.stringify(cart));
	} catch (ex) {
		ErrorUtil.log(
			"E_COULDNT_SAVE_CART_TO_STORAGE",
			"Couldn't save cart to storage.",
			cart
		);
		// no-op
	}
}

function saveCartToServer(cart, cb) {
	const payload = {cart};
	return runAjax(
		{
			endpoint: "/api/store/saveCart",
			payload,
			timeout: AJAX_TIMEOUT,
		},
		cb
	);
}

function logMarketingAddToCartIfNeeded(cart, initialItemCount) {
	// Note: this will never do anything if initialItemCount is not passed.
	if (cart.cartID && getItemCount(cart) > initialItemCount) {
		logMarketingEvent(
			initialItemCount ? "AddToCart" : "StartCart",
			cart.cartID
		);
	}
}

function trySaveCartToServer(cart, initialItemCount) {
	let doingFirstSaveHere = false;
	if (!cart.cartID && cartState.doingFirstSave) {
		cartState.needsSave = true;
		return;
	} else if (!cart.cartID) {
		doingFirstSaveHere = true;
		cartState.doingFirstSave = true;
	}
	saveCartToServer(cart, (err, res) => {
		if (doingFirstSaveHere) {
			cartState.doingFirstSave = false;
		}
		if (err) {
			retrySaveCartToServer();
			return;
		}

		if (res && res.cartID) {
			const reloadedCart = loadCartFromStorage();
			if (reloadedCart && !reloadedCart.cartID) {
				reloadedCart.cartID = parseInt(res.cartID, 10);
				saveCartToStorage(reloadedCart);
				// We also call this in modifyCart, but it would not have done anything
				// if the cart had no ID yet. So doing it here should never double log
				// the event.
				logMarketingAddToCartIfNeeded(reloadedCart, initialItemCount);
			}
		}
		if (cartState.needsSave) {
			cartState.needsSave = false;
			retrySaveCartToServer();
		}
	});
}

function retrySaveCartToServer(initialItemCount) {
	setTimeout(() => {
		const cart = loadCartFromStorage();
		trySaveCartToServer(cart, initialItemCount);
	}, 10000);
}

// Pass a function that takes a cart and mutates it. This will handle saving
// the cart to both storage and the server. Note the server save is in the
// background, so this function may be treated as sync.
function modifyCart(callback) {
	const cart = loadCartFromStorage();
	const initialItemCount = getItemCount(cart);
	callback(cart);
	cart.lastItemsUpdate = Date.now();
	if (checkLocalStorageIsFull()) {
		clearLocalStorageTempData();
	}
	saveCartToStorage(cart);
	updateTopBarCart(getItemCountWithBundlesAndSets(cart.items));
	trySaveCartToServer(cart, initialItemCount);
	logMarketingAddToCartIfNeeded(cart, initialItemCount);
}

function showAddToCartModal(numItems, itemWord, buttonEl) {
	// For subscriptions we show the enhanced modal that doesn't ask for a student name
	// and adds subscription items to the cart immediately (names collected at checkout).

	// Simple text modal - this is used for books and shirts.
	if (itemWord !== "subscription") {
		// At the time of writing, itemWord can be book, subscription, bundle. These
		// all have standard pluralization.
		const itemSingular = itemWord.endsWith("s")
			? itemWord.slice(0, -1)
			: itemWord;
		const itemCorrectWord = itemSingular + (numItems !== 1 ? "s" : "");
		const modalContentTemplate = compileTemplate("#addCartModalBody");
		const modalContentContext = {
			addedItemWords: `${numItems} ${itemCorrectWord}`,
		};
		const modalContentRendered = modalContentTemplate(modalContentContext);

		const modalEl = openModal(
			{
				contents: modalContentRendered,
			},
			buttonEl
		);
		const continueEl = modalEl.querySelector(
			".ba11-add-cart-modal-button.ba11--continue"
		);
		if (continueEl) {
			onClickOrEnter(continueEl, evt => {
				evt.preventDefault();
				closeCurrentModal();
			});
		}
		return;
	}

	// New modal (enhanced version with item details, used for non-bundle subscriptions)
	const cart = loadCartFromStorage();

	// Determine the item from buttonEl class instead of cart's last item
	let lastItem = null;
	let sku = null;

	if (buttonEl) {
		if (buttonEl.classList.contains("beast-monthly")) {
			sku = "beast-monthly";
			lastItem = {sku: "beast-monthly", quantity: 1, options: {}};
		} else if (buttonEl.classList.contains("beast-yearly")) {
			sku = "beast-yearly";
			// Try to find the yearly item in cart to get options (like bundleID)
			const yearlyItem =
				cart.items && cart.items.find(item => item.sku === "beast-yearly");
			lastItem = yearlyItem || {
				sku: "beast-yearly",
				quantity: 1,
				options: {},
			};
		} else if (buttonEl.classList.contains("beast-bundle")) {
			sku = "beast-bundle";
			lastItem = {sku: "beast-bundle", quantity: 1, options: {}};
		}
	}

	// Fallback to cart's last item if buttonEl doesn't have the class
	if (!lastItem && cart.items && cart.items.length > 0) {
		lastItem = cart.items[cart.items.length - 1];
		sku = lastItem.sku;
	}

	// Get item details
	let itemDescription = "";
	let itemPrice = "";

	if (lastItem && sku) {
		if (sku.startsWith("beast-")) {
			if (sku === "beast-monthly") {
				itemDescription = "Monthly Subscription";
			} else if (sku === "beast-yearly") {
				if (lastItem.options && lastItem.options.bundleID) {
					itemDescription =
						"Bundle: 1-year subscription to BA Online + one full level of books";
				} else {
					itemDescription = "Yearly Subscription";
				}
			} else if (sku.includes("bundle")) {
				itemDescription = lastItem.options?.displayName || "Bundle";
			}

			// Calculate price for the item
			const itemPriceValue = ElevPrices.getBaseItemPriceForSku(sku);
			itemPrice = ElevPrices.finishPrice(itemPriceValue);
			if (sku === "beast-monthly") {
				itemPrice += "/mo";
			} else {
				itemPrice += "/year";
			}
		}
	}

	// Get cart totals
	const cartItemCount = getItemCountWithBundlesAndSets(cart.items);
	const cartSubtotal = ElevPrices.finishPrice(
		ElevPrices.calculateTotalItemPrice(cart.items)
	);

	const subCount = countSubscriptionsInCart(cart);
	// Sibling discount is per subscription type (SKU): only 2+ of the same type get it.
	const subCountBySku = {};
	(cart.items || []).forEach(item => {
		if (
			(item.sku === "beast-yearly" || item.sku === "beast-monthly") &&
			!(item.options && item.options.bundleID)
		) {
			subCountBySku[item.sku] =
				(subCountBySku[item.sku] || 0) + (item.quantity || 1);
		}
	});
	const showSiblingDiscount = (subCountBySku[sku] || 0) >= 2;

	let siblingPrice = "";
	if (
		showSiblingDiscount &&
		sku &&
		(sku === "beast-monthly" || sku === "beast-yearly")
	) {
		const multi = ElevPrices.getMultiPriceForSku(sku);
		if (multi != null) {
			siblingPrice = ElevPrices.finishPrice(multi);
			siblingPrice += sku === "beast-monthly" ? "/mo" : "/year";
		}
	}

	const modalContentTemplate = compileTemplate("#addCartModalBodyNew");
	if (typeof modalContentTemplate !== "function") {
		return;
	}
	const modalContentContext = {
		itemDescription: itemDescription,
		itemPrice: itemPrice,
		cartItemCount: cartItemCount,
		cartItemWord: cartItemCount === 1 ? "item" : "items",
		cartSubtotal: cartSubtotal,
		showSiblingDiscount: showSiblingDiscount,
		siblingPrice: siblingPrice,
	};

	const modalContentRendered = modalContentTemplate(modalContentContext);

	if (!modalContentRendered || typeof modalContentRendered !== "string") {
		return;
	}

	// Manually create modal structure without modalTemplate wrapper
	closeCurrentModal();

	// Create a container div and insert the rendered template
	const modalContainer = document.createElement("div");
	modalContainer.innerHTML = modalContentRendered;
	const modalDiv =
		modalContainer.querySelector(".modal-wrapper") ||
		modalContainer.firstElementChild;

	if (!modalDiv) {
		return;
	}

	const modalsContainer = document.querySelector("#modalsContainer");
	if (!modalsContainer) {
		return;
	}
	modalsContainer.appendChild(modalDiv);

	// Remove hide class if present and ensure modal is visible
	modalDiv.classList.remove("hide");

	// Set display to flex (modal-wrapper has display: none by default)
	modalDiv.style.display = "flex";

	// Add show class to modal-wrap if it exists
	const modalWrap = modalDiv.querySelector(".add-cart-modal-wrap, .modal-wrap");
	if (modalWrap) {
		modalWrap.classList.add("show", "anim-fade-in");
	}

	// Show max-subs message when cart already has 6+ subscriptions (same as enroll modal)
	if (!numItems && subCount >= MAX_SUBS) {
		modalWrap?.classList.add("has-max-subs");
		const titleEl = modalDiv.querySelector(".ba11-add-cart-modal-title");
		if (titleEl) {
			titleEl.innerText =
				"You have reached the maximum number of subscriptions.";
		}
	}

	// Set up close handler
	const closeModalFunc = function() {
		modalDiv.remove();
		if (buttonEl) buttonEl.focus();
	};

	// Set up backdrop click handler (modal-closer)
	const backdrop = modalDiv.querySelector(
		".add-cart-modal-closer, .modal-closer"
	);
	if (backdrop) {
		backdrop.addEventListener("click", closeModalFunc);
	}

	// Set up escape key handler
	modalDiv.addEventListener("keydown", evt => {
		if (evt.key === "Escape" || evt.code === "Escape" || evt.which === 27) {
			closeModalFunc();
		}
	});

	const modalEl = modalDiv;

	// Set up close button (close-block)
	const closeButton = modalEl.querySelector(
		".add-cart-modal-close-block, .add-cart-modal-close-image, .close-block, .close-image"
	);
	const keepShoppingButton = modalEl.querySelector(".keep-shopping-btn");
	if (closeButton) {
		onClickOrEnter(closeButton, evt => {
			evt.preventDefault();
			closeModalFunc();
		});
	}

	if (keepShoppingButton) {
		onClickOrEnter(keepShoppingButton, evt => {
			evt.preventDefault();
			let enrollType = null;
			if (sku && sku.includes("bundle")) {
				enrollType = "bundle";
			} else if (sku === "beast-monthly") {
				enrollType = "monthly";
			} else if (sku === "beast-yearly") {
				enrollType = "yearly";
			}
			track("Enrollment Keep Shopping Button Clicked", {
				page: "enroll",
				enrollType,
			});
			closeModalFunc();
		});
	}

	const viewCartButton = modalEl.querySelector(".add-to-cart-btn");
	if (viewCartButton) {
		onClickOrEnter(viewCartButton, evt => {
			let enrollType = null;
			if (sku && sku.includes("bundle")) {
				enrollType = "bundle";
			} else if (sku === "beast-monthly") {
				enrollType = "monthly";
			} else if (sku === "beast-yearly") {
				enrollType = "yearly";
			}
			track("Enrollment Checkout Button Clicked", {
				page: "enroll",
				enrollType,
			});
		});
	}
}

function showAddBundleToCartModal(numItems, buttonEl) {
	const cart = loadCartFromStorage();
	const modalContentTemplate = compileTemplate("#addBundleToCartModalBody");
	if (typeof modalContentTemplate !== "function") {
		return;
	}
	const itemDescription =
		"Bundle: 1-year subscription to BA Online + one full level of books";
	const itemPrice = ElevPrices.finishPrice(
		ElevPrices.getBundlePriceForGrade(3)
	);
	const cartItemCount = getItemCountWithBundlesAndSets(cart.items);
	const cartSubtotal = ElevPrices.finishPrice(
		ElevPrices.calculateTotalItemPrice(cart.items)
	);
	const showDeferment = getFeatureFlag("ENABLE_BA_BUNDLE_DEFERMENT");
	track("Enrollment Add Bundle To Cart Modal Opened", {
		page: "enroll",
		enrollType: "bundle",
		showDeferment: showDeferment,
	});
	const modalContentContext = {
		itemDescription,
		itemPrice,
		cartItemCount,
		cartItemWord: cartItemCount === 1 ? "item" : "items",
		cartSubtotal,
		showDeferment,
	};
	const modalContentRendered = modalContentTemplate(modalContentContext);
	closeCurrentModal();

	// Same approach as showAddToCartModal (addCartModalBodyNew): append .modal-wrapper
	// directly so it's visible. openModal() wraps content in modalTemplate and the inner
	// .modal-wrapper gets display:none from Webflow CSS, causing a blank white modal.
	const modalContainer = document.createElement("div");
	modalContainer.innerHTML = modalContentRendered;
	const modalDiv =
		modalContainer.querySelector(".modal-wrapper") ||
		modalContainer.firstElementChild;
	if (!modalDiv) return;

	const modalsContainer = document.querySelector("#modalsContainer");
	if (!modalsContainer) return;
	modalsContainer.appendChild(modalDiv);

	modalDiv.classList.remove("hide");
	modalDiv.style.display = "flex";
	const modalWrap = modalDiv.querySelector(".add-cart-modal-wrap, .modal-wrap");
	if (modalWrap) modalWrap.classList.add("show", "anim-fade-in");

	const closeModalFunc = function() {
		modalDiv.remove();
		if (buttonEl) buttonEl.focus();
	};

	const backdrop = modalDiv.querySelector(
		".add-cart-modal-closer, .modal-closer"
	);
	if (backdrop) backdrop.addEventListener("click", closeModalFunc);
	modalDiv.addEventListener("keydown", evt => {
		if (evt.key === "Escape" || evt.code === "Escape" || evt.which === 27) {
			closeModalFunc();
		}
	});

	const closeBlock = modalDiv.querySelector(
		".add-cart-modal-close-block, .add-cart-modal-close-image, .close-block, .close-image"
	);
	const keepShoppingBtn = modalDiv.querySelector(".keep-shopping-btn");
	const viewCartBtn = modalDiv.querySelector(".add-to-cart-btn");
	if (closeBlock) {
		onClickOrEnter(closeBlock, evt => {
			evt.preventDefault();
			closeModalFunc();
		});
	}

	if (keepShoppingBtn) {
		onClickOrEnter(keepShoppingBtn, evt => {
			evt.preventDefault();
			track("Enrollment Keep Shopping Button Clicked", {
				page: "enroll",
				enrollType: "bundle",
			});
			closeModalFunc();
		});
	}
	if (viewCartBtn) {
		viewCartBtn.classList.add("add-cart-modal-btn--disabled");
		viewCartBtn.setAttribute("aria-disabled", "true");
		viewCartBtn.setAttribute("tabindex", "-1");
		onClickOrEnter(viewCartBtn, evt => {
			evt.preventDefault();
			if (viewCartBtn.classList.contains("add-cart-modal-btn--disabled")) {
				return;
			}
			const dropdownWrap = modalDiv.querySelector(".add-cart-modal-dropdown");
			const selectedGrade = dropdownWrap?.getAttribute("data-selected-grade");
			if (selectedGrade) {
				const subscriptionCount = countSubscriptionsInCart(cart);
				const subName = getSubscriptionPlaceholderName(subscriptionCount);
				const selectedDeferment = modalDiv.querySelector(
					'input[name="bundleDeferment"]:checked'
				)?.value;
				const deferred = showDeferment && selectedDeferment === "placement";
				let itemsToAdd = getItemsForSubType("bundle", subName, [selectedGrade]);
				if (deferred) {
					itemsToAdd = withDeferredBookLevel(itemsToAdd, selectedGrade);
				}
				track("Enrollment Checkout Button Clicked", {
					page: "enroll",
					enrollType: "bundle",
					selectedGrade,
					deferred,
				});
				modifyCart(function(c) {
					addItemsToCart(itemsToAdd, c);
				});
			}
			closeModalFunc();
			window.location.href = "/checkout";
		});
	}

	if (window.Webflow && window.Webflow.require) {
		window.Webflow.require("dropdown").ready();
	}

	// Wire bundle level dropdown: on option click, update label, store grade, close dropdown, enable button
	const dropdownWrap = modalDiv.querySelector(".add-cart-modal-dropdown");
	if (dropdownWrap) {
		const dropdownParent = dropdownWrap.querySelector(".w-dropdown");
		const dropdownLabel = dropdownWrap.querySelector(".dropdown-label");
		const bundleLinks = dropdownWrap.querySelectorAll(".bundle-select");
		bundleLinks.forEach(function(link) {
			link.addEventListener("click", function(e) {
				e.preventDefault();
				const grade = link.getAttribute("data-grade");
				if (grade) {
					dropdownWrap.setAttribute("data-selected-grade", grade);
				}
				if (dropdownLabel) {
					dropdownLabel.textContent = link.textContent.trim();
				}
				if (window.$ && dropdownParent) {
					window.$(dropdownParent).trigger("w-close");
				} else if (dropdownParent) {
					dropdownParent
						.querySelector(".w-dropdown-toggle")
						?.classList.remove("w--open");
					dropdownParent
						.querySelector(".w-dropdown-list")
						?.classList.remove("w--open");
				}
				if (viewCartBtn) {
					viewCartBtn.classList.remove("add-cart-modal-btn--disabled");
					viewCartBtn.removeAttribute("aria-disabled");
					viewCartBtn.setAttribute("tabindex", "0");
				}
			});
		});
	}

	// Wire deferment radios: highlight the selected card. The checked state is
	// read directly off the inputs when ADD TO CART is clicked.
	const defermentOptions = modalDiv.querySelectorAll(
		".add-cart-modal-deferment-option"
	);
	defermentOptions.forEach(function(option) {
		const input = option.querySelector(".add-cart-modal-deferment-radio");
		if (!input) return;
		input.addEventListener("change", function() {
			defermentOptions.forEach(function(other) {
				other.classList.toggle(
					"add-cart-modal-deferment-option--selected",
					other === option
				);
			});
		});
	});
	return;
}

function updateTopBarCart(itemCount) {
	$each(document, ".ba11-top-cart-num > span", function(el) {
		el.innerHTML = itemCount;
	});
}

function getItemTypeFromSku(sku) {
	if (sku.startsWith("bundle")) {
		return ShopBundleData[sku].type;
	}
	return ShopData[sku].type;
}

module.exports = {
	getDefaultCart,
	getItemsForSubType,
	addItemsToCart,
	removeItemFromCart,
	modifyCart,
	showAddToCartModal,
	showAddBundleToCartModal,
	loadCartFromStorage,
	saveCartToStorage,
	updateTopBarCart,
	getItemCount,
	getGradeSetItems,
	getBundleItems,
	getTotalQuantity,
	getItemTypeFromSku,
	canAddSubToCart,
	getScienceGradeSetItems,
	getSubscriptionPlaceholderName,
};
